import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useChecklistsQuery } from "../api/queries";
import { ChecklistTour, useChecklistTour } from "../tour/ChecklistTour";
import { ChecklistsFilter } from "../ui/ChecklistsFilter";
import { ChecklistsTable } from "../ui/ChecklistsTable";
import { ChecklistsToolbar } from "../ui/ChecklistsToolbar";

export function ChecklistsPage() {
  const nav = useNavigate({ from: "/app/checklists" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/checklists" });
  const tour = useChecklistTour();

  const list = useChecklistsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["checklists:create"]);

  return (
    <>
      <Header />

      <Main>
        <ChecklistsToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/checklists/new" })}
          onStartTour={tour.startTour}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ChecklistsFilter />

          <ChecklistsTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(c) =>
              nav({ to: "/app/checklists/$id/edit", params: { id: c.id } })
            }
            onView={(id) => nav({ to: "/app/checklists/$id", params: { id } })}
          />
        </div>

        <ChecklistTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
      </Main>
    </>
  );
}
